import React, { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { motion, AnimatePresence } from 'framer-motion';

// ── Orbiting star sparks ──────────────────────────────────────────────────────
const SPARKS = Array.from({ length: 18 }, (_, i) => ({
  angle: (i / 18) * 360,
  r:     160 + (i % 3) * 40,
  size:  4 + (i % 4) * 3,
  delay: i * 0.06,
  dur:   1.8 + (i % 3) * 0.5,
}));

// ── Burst rays ────────────────────────────────────────────────────────────────
const RAYS = Array.from({ length: 12 }, (_, i) => ({
  angle: i * 30,
  delay: i * 0.05,
}));

export default function WinnerScreen({ winner, onRestart, text }) {
  const [viewportSize, setViewportSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const isDraw = Boolean(winner?.isDraw);
  const isRed = winner.color === 'red';

  const primaryColor   = isDraw ? '#d7ad4a' : isRed ? '#ef4444' : '#3b82f6';
  const secondaryColor = isDraw ? '#fde68a' : isRed ? '#fca5a5' : '#93c5fd';
  const glowRgba       = isDraw ? 'rgba(215,173,74,0.7)' : isRed ? 'rgba(239,68,68,0.7)'   : 'rgba(59,130,246,0.7)';
  const glowSoft       = isDraw ? 'rgba(215,173,74,0.25)' : isRed ? 'rgba(239,68,68,0.25)'  : 'rgba(59,130,246,0.25)';
  const bgFrom         = isDraw ? 'from-amber-950' : isRed ? 'from-red-950'          : 'from-blue-950';
  const confettiColors = isDraw
    ? ['#d7ad4a','#fde68a','#fff','#0f5f48','#f97316']
    : isRed
    ? ['#ef4444','#fca5a5','#fde047','#fff','#f97316']
    : ['#3b82f6','#93c5fd','#fde047','#fff','#a78bfa'];

  // Crown bounce loop
  const crownAnim = {
    y:     [0, -14, 0],
    scale: [1, 1.08, 1],
  };

  // Pulse on winner name
  const namePulse = {
    textShadow: [
      `0 0 20px ${glowRgba}, 0 0 60px ${glowSoft}`,
      `0 0 50px ${glowRgba}, 0 0 120px ${glowRgba}`,
      `0 0 20px ${glowRgba}, 0 0 60px ${glowSoft}`,
    ],
  };

  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 200); return () => clearTimeout(t); }, []);

  useEffect(() => {
    const handleResize = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const audio = new Audio('/win.mp3');
    audio.play().catch(err => console.log('Winner audio error:', err));
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  return (
    <div
      dir={text.dir}
      className={`min-h-screen bg-gradient-to-b ${bgFrom} via-gray-950 to-black text-white flex flex-col items-center justify-center overflow-hidden relative px-4 py-8`}
    >
      {/* ── Confetti ── */}
      <Confetti width={viewportSize.width} height={viewportSize.height} recycle={true} numberOfPieces={viewportSize.width < 640 ? 140 : 320} colors={confettiColors} />

      {/* ── Background radial glow ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 55% at 50% 50%, ${glowSoft} 0%, transparent 70%)`,
        }}
      />

      {/* ── Burst rays (behind everything) ── */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 0 }}>
        {RAYS.map((ray, i) => (
          <motion.div
            key={i}
            initial={{ scaleY: 0, opacity: 0 }}
            animate={{ scaleY: [0, 1, 0.6], opacity: [0, 0.18, 0.08] }}
            transition={{ delay: 0.3 + ray.delay, duration: 1.2, ease: 'easeOut' }}
            style={{
              position:        'absolute',
              width:           3,
              height:          320,
              borderRadius:    2,
              background:      `linear-gradient(to top, transparent, ${primaryColor})`,
              transformOrigin: 'bottom center',
              rotate:          `${ray.angle}deg`,
              bottom:          '50%',
            }}
          />
        ))}
      </div>

      {/* ── Orbiting sparks ── */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 1 }}>
        {SPARKS.map((sp, i) => {
          const rad = (sp.angle * Math.PI) / 180;
          return (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{ width: sp.size, height: sp.size, background: i % 3 === 0 ? '#fde047' : secondaryColor }}
              animate={{
                x: [
                  Math.cos(rad) * sp.r * 0.4,
                  Math.cos(rad) * sp.r,
                  Math.cos(rad) * sp.r * 0.4,
                ],
                y: [
                  Math.sin(rad) * sp.r * 0.4,
                  Math.sin(rad) * sp.r,
                  Math.sin(rad) * sp.r * 0.4,
                ],
                opacity: [0, 0.9, 0],
                scale:   [0, 1.2, 0],
              }}
              transition={{ delay: sp.delay, duration: sp.dur, repeat: Infinity, ease: 'easeInOut' }}
            />
          );
        })}
      </div>

      {/* ── Main card ── */}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ scale: 0.3, opacity: 0, y: 60 }}
            animate={{ scale: 1,   opacity: 1, y: 0  }}
            transition={{ type: 'spring', bounce: 0.5, duration: 1.2 }}
            className="relative z-10 flex flex-col items-center gap-4 sm:gap-6 px-4 sm:px-8 py-8 sm:py-10 text-center w-full max-w-4xl"
          >

            {/* Title Above Crown */}
            <motion.div
              initial={{ y: -40, opacity: 0 }}
              animate={{ y: 0,   opacity: 1  }}
              transition={{ delay: 0.3, type: 'spring', bounce: 0.6 }}
              className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-200 mt-2 sm:mt-4 leading-tight"
              dir={text.dir}
            >
              {isDraw ? text.drawTitle : text.winnerTitle}
            </motion.div>

            {/* Crown icon */}
            <motion.div
              animate={crownAnim}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="text-5xl sm:text-6xl md:text-7xl drop-shadow-[0_0_25px_rgba(253,224,71,0.9)]"
            >
              👑
            </motion.div>

            {/* Ornamental divider */}
            <motion.div
              initial={{ scaleX: 0 }} animate={{ scaleX: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="flex items-center gap-3"
            >
              <div className="h-px w-24 rounded-full" style={{ background: `linear-gradient(to right, transparent, ${primaryColor})` }} />
              <span className="text-yellow-400 text-lg">✦</span>
              <div className="h-px w-24 rounded-full" style={{ background: `linear-gradient(to left, transparent, ${primaryColor})` }} />
            </motion.div>

            {/* Winner name */}
            <motion.h1
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1,   opacity: 1  }}
              transition={{ delay: 1.0, type: 'spring', bounce: 0.55, duration: 1.0 }}
              animate2={namePulse}
              className="text-4xl sm:text-6xl md:text-8xl font-black tracking-wide break-words"
              style={{
                background: `linear-gradient(135deg, #fff 0%, ${secondaryColor} 40%, ${primaryColor} 70%, #fde047 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: `drop-shadow(0 0 28px ${glowRgba}) drop-shadow(0 4px 12px rgba(0,0,0,0.8))`,
              }}
            >
              {winner.name}
            </motion.h1>

            {winner.score && (
              <div className="rounded-2xl border border-yellow-300/40 bg-white/10 px-5 py-3 text-base sm:text-xl font-black text-yellow-100">
                {text.finalScore}: {winner.score.team1} - {winner.score.team2}
              </div>
            )}

            {isDraw && (
              <div className="max-w-xl text-sm sm:text-lg font-bold text-white/75">
                {text.drawSubtitle}
              </div>
            )}

            {/* Glow pulse under name */}
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.15, 0.9] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute pointer-events-none rounded-full"
              style={{
                width: viewportSize.width < 640 ? 260 : 420, height: viewportSize.width < 640 ? 72 : 100,
                background: `radial-gradient(ellipse at center, ${glowRgba} 0%, transparent 70%)`,
                filter: 'blur(18px)',
                top: '55%', left: '50%', transform: 'translate(-50%, -50%)',
                zIndex: -1,
              }}
            />

            {/* Star row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
              className="flex gap-2 sm:gap-3 text-2xl sm:text-3xl"
            >
              {['⭐','🏆','⭐'].map((e, i) => (
                <motion.span
                  key={i}
                  animate={{ y: [0, -8, 0], rotate: [0, i === 1 ? 15 : -10, 0] }}
                  transition={{ duration: 1.8, delay: i * 0.2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  {e}
                </motion.span>
              ))}
            </motion.div>

            {/* Restart button */}
            <motion.button
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0,  opacity: 1  }}
              transition={{ delay: 2.0, type: 'spring' }}
              whileHover={{ scale: 1.07 }}
              whileTap={{ scale: 0.94 }}
              onClick={onRestart}
              className="mt-4 w-full max-w-sm px-8 sm:px-14 py-4 sm:py-5 rounded-2xl font-extrabold text-lg sm:text-2xl text-white shadow-2xl border-2 transition-all"
              style={{
                background:   `linear-gradient(135deg, ${primaryColor}, ${isRed ? '#dc2626' : '#2563eb'})`,
                borderColor:  secondaryColor,
                boxShadow:    `0 0 30px ${glowSoft}, 0 8px 32px rgba(0,0,0,0.5)`,
              }}
            >
              {text.playAgain}
            </motion.button>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
